// Play / Puzzle detail: the category over its answer slots; the titled
// phrase with blanks for the unfound words and everything else visible; or
// the quotation in quotation marks with its attribution under it. Tapping it
// closes it, as the progress fraction above it does. It opens with the
// Puzzle, and a shine runs across it once as the level begins.
import { promptText, type PlayLevel } from '@wits/core';
import type { Blank } from '@wits/core';
import type { PlayRecord } from '../../systems/api/types';

interface Props {
  level: PlayLevel;
  play: PlayRecord;
  shine: boolean;
  onShineEnd: () => void;
  onClose: () => void;
}

export function PuzzleDetail({ level, play, shine, onShineEnd, onClose }: Props) {
  if (level.mode !== 'puzzle' || !play.puzzle) return null;
  return (
    <div
      className={`panel puzzle-detail${shine ? ' is-shining' : ''}`}
      onClick={onClose}
      onAnimationEnd={(e) => e.animationName === 'goal-shine' && onShineEnd()}
    >
      <Goal puzzle={level.puzzle} blanks={play.puzzle.blanks} />
    </div>
  );
}

function Goal({ puzzle, blanks }: { puzzle: Extract<PlayLevel, { mode: 'puzzle' }>['puzzle']; blanks: Blank[] }) {
  if (puzzle.type === 'category') {
    return (
      <>
        <h2 className="puzzle-category">{puzzle.category}</h2>
        <ol className="puzzle-slots">
          {blanks.map((b, i) => (
            <li key={i} className={`puzzle-slot mono${b.word ? ' is-found' : ''}`}>
              {b.word ?? '_ '.repeat(b.length).trim()}
            </li>
          ))}
        </ol>
      </>
    );
  }
  const text = <TextWithBlanks text={promptText(puzzle) ?? ''} blanks={blanks} />;
  if (puzzle.type === 'quotation') {
    return (
      <>
        <p className="puzzle-phrase">
          <span className="puzzle-quote">“</span>
          {text}
          <span className="puzzle-quote">”</span>
        </p>
        <p className="puzzle-attribution quiet">— {puzzle.attribution}</p>
      </>
    );
  }
  return (
    <>
      <h2 className="puzzle-title label-mono">{puzzle.title}</h2>
      <p className="puzzle-phrase">{text}</p>
    </>
  );
}

/** The text with each {n} placeholder as its blank: underscores, or the word once found. */
function TextWithBlanks({ text, blanks }: { text: string; blanks: Blank[] }) {
  const parts = text.split(/(\{\d+\})/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = /^\{(\d+)\}$/.exec(part);
        if (!m) return <span key={i}>{part}</span>;
        const blank = blanks[Number(m[1]) - 1];
        if (!blank) return null;
        return (
          <span key={i} className={`puzzle-blank mono${blank.word ? ' is-found' : ''}`}>
            {blank.word ?? '_'.repeat(blank.length)}
          </span>
        );
      })}
    </>
  );
}
